import React from 'react';
import { useOrganizationClientRecords } from '../../../../hooks/api/useOrganizationClientRecords';
import { AnimateLoadedContent } from '../../../v2BaseComponents/Loader';
import { APITypes, OrganizationTypes } from '../../../../primary-record-types';
import { clientRecordDetailsToClientRecord, clientRecordStatusTypeGuard } from '../../../../utils/clientRecordsUtil';
import urls from '../../../../utils/urls';
import { LoadableAvatar, getFallbackFromName } from '../../../v2BaseComponents/Avatar';
import Text from '../../../v2BaseComponents/Text';
import getName from '../../../../utils/getName';
import Stack, { contentStackSpacing } from '../../../v2BaseComponents/Stack';
import useOrganizationMedicalProfiles from '../../../../hooks/api/useOrganizationMedicalProfiles';
import LabelValueDisplay, { LabelValueItem } from '../../../v2BaseComponents/LabelValueDisplay';
import { getIntl } from '../../../../utils/intl';
import getDateString from '../../../../utils/getDateString';
import addressToString from '../../../../utils/addressToString';
import { clientRecordStatus } from '../../../../utils/sharedMessages';

const intl = getIntl('client-record-details', {
    pointOfContact: 'Point of Contact',
    pointOfContactEmail: 'Point of Contact Email',
    name: 'Name',
    groups: 'Groups',
    dob: 'Date of Birth',
    sex: 'Sex',
    gender: 'Gender',
    address: 'Address',
    email: 'Email',
    phone: 'Phone',
    fax: 'Fax',
    profileOrganizer: 'Profile Organizer',
    profileOrganizerEmail: 'Profile Organizer Email',
    clientStartDate: 'Client Start Date Time',
    clientEndDate: 'Client End Date',
    clientRecordCreateDateTime: 'Client Record Create Date Time',
    inviteSentDateTime: 'Invite Sent Date Time',
    inviteRejectedDateTime: 'Invite Rejected Date Time',
    status: 'Status',
    endDate: 'End Date',
    profileOrganizerPhoneNumber: 'Profile Organizer Phone Number',
});

export type ClientRecordDetailsOwnProps = {
    organizationClientRecordId: string;
};

export type ClientRecordDetailsProps = ClientRecordDetailsOwnProps;

const Content: React.FC<{
    clientRecord: OrganizationTypes.OrganizationClientRecord;
    organizationClientRecordGroups: APITypes.organizationService.OrganizationGroupDetails[];
    organizationMedicalProfile: APITypes.organizationService.OrganizationMedicalProfileDetails | null;
}> = ({ clientRecord, organizationClientRecordGroups, organizationMedicalProfile }) => {
    const maybeRenderAvatar = () => {
        if (!clientRecordStatusTypeGuard.active(clientRecord)) return null;

        return (
            <LoadableAvatar
                avatarUrl={urls.api.medicalProfiles.avatar(clientRecord.clientMedicalProfileId)}
                size="xl"
                fallbackName={getFallbackFromName(
                    clientRecord.clientDetails.firstName,
                    clientRecord.clientDetails.lastName,
                )}
            />
        );
    };

    const maybeRenderName = () => {
        if (!clientRecord.clientDetails.firstName && !clientRecord.clientDetails.lastName) return null;

        return <Text variant="h4">{getName(clientRecord.clientDetails)}</Text>;
    };

    const getMedicalProfileDetails = (): LabelValueItem[] => {
        if (!organizationMedicalProfile) return [];

        return [
            {
                label: intl('groups'),
                value: organizationClientRecordGroups
                    .map((group) => group.metadata.name)
                    .sort((a, b) => a.localeCompare(b))
                    .join(', '),
            },
            {
                label: intl('dob'),
                value: getDateString(organizationMedicalProfile!.contactDetails.dob, 'date'),
            },
            {
                label: intl('sex'),
                value: organizationMedicalProfile!.contactDetails.sex,
            },
            {
                label: intl('gender'),
                value: organizationMedicalProfile!.contactDetails.gender,
            },
            {
                label: intl('address'),
                value: addressToString(organizationMedicalProfile!.contactDetails),
            },
            {
                label: intl('email'),
                value: organizationMedicalProfile!.contactDetails.email,
                linkType: 'email',
            },
            {
                label: intl('phone'),
                value: organizationMedicalProfile!.contactDetails.phoneNumber,
                linkType: 'phone',
            },
            {
                label: intl('fax'),
                value: organizationMedicalProfile!.contactDetails.faxNumber,
            },
        ];
    };

    const getInvitationDetails = (): LabelValueItem[] => {
        if (
            !clientRecordStatusTypeGuard.invitationRejected(clientRecord) &&
            !clientRecordStatusTypeGuard.active(clientRecord) &&
            !clientRecordStatusTypeGuard.inactive(clientRecord)
        )
            return [];

        const returnValue = [
            {
                label: intl('inviteSentDateTime'),
                value: getDateString(clientRecord.invitationDetails.sentDateTime, 'date-time'),
            },
        ];

        if (clientRecordStatusTypeGuard.invitationRejected(clientRecord)) {
            returnValue.push({
                label: intl('inviteRejectedDateTime'),
                value: getDateString(clientRecord.invitationDetails.sentDateTime, 'date-time'),
            });
        }

        return returnValue;
    };

    const avatar = maybeRenderAvatar();
    const name = maybeRenderName();

    return (
        <Stack vertical fullWidth align="center" space={contentStackSpacing}>
            {avatar || name ? (
                <Stack vertical fullWidth align="center" space={2}>
                    {avatar}
                    {name}
                </Stack>
            ) : null}
            <LabelValueDisplay
                omitEmptyValues
                items={[
                    { label: intl('status'), value: clientRecordStatus(clientRecord.status) },
                    { label: intl('name'), value: getName(clientRecord.clientDetails) },
                    ...getMedicalProfileDetails(),
                    {
                        label: intl('profileOrganizer'),
                        value: getName(clientRecord.clientOrganizerDetails),
                    },
                    {
                        label: intl('profileOrganizerEmail'),
                        value: clientRecord.clientOrganizerDetails.email,
                        linkType: 'email',
                    },
                    {
                        label: intl('profileOrganizerPhoneNumber'),
                        value: clientRecord.clientOrganizerDetails.phoneNumber,
                        linkType: 'phone',
                    },
                    {
                        label: intl('clientRecordCreateDateTime'),
                        value: getDateString(clientRecord.createdDateTime, 'date-time'),
                    },
                    ...getInvitationDetails(),
                    {
                        label: intl('clientStartDate'),
                        value:
                            clientRecordStatusTypeGuard.active(clientRecord) ||
                            clientRecordStatusTypeGuard.inactive(clientRecord)
                                ? getDateString(clientRecord.startDateTime, 'date-time')
                                : null,
                    },
                    {
                        label: intl('clientEndDate'),
                        value: clientRecordStatusTypeGuard.inactive(clientRecord)
                            ? getDateString(clientRecord.endDateTime, 'date-time')
                            : null,
                    },
                    {
                        label: intl('endDate'),
                        value: clientRecordStatusTypeGuard.active(clientRecord)
                            ? getDateString(clientRecord.expirationDateTime, 'date')
                            : null,
                    },
                ]}
            />
        </Stack>
    );
};

const ClientRecordDetails: React.FC<ClientRecordDetailsProps> = (props) => {
    const { isLoadingClientRecord, clientRecord } = useOrganizationClientRecords({
        organizationClientRecordId: props.organizationClientRecordId,
    });

    const getShouldLoadOrganizationMedicalProfile = () => {
        if (!clientRecord) return false;

        return clientRecordStatusTypeGuard.active(clientRecord);
    };

    const { isLoadingOrganizationMedicalProfile, organizationMedicalProfile } = useOrganizationMedicalProfiles({
        medicalProfileId: getShouldLoadOrganizationMedicalProfile()
            ? clientRecord?.clientMedicalProfileId || undefined
            : undefined,
    });

    const { isLoadingClientRecordGroups, clientRecordGroups } = useOrganizationClientRecords({
        organizationClientRecordId: props.organizationClientRecordId,
    });

    return (
        <AnimateLoadedContent
            isLoading={
                isLoadingClientRecord ||
                !clientRecord ||
                isLoadingOrganizationMedicalProfile ||
                isLoadingClientRecordGroups
            }
        >
            {() => (
                <Content
                    clientRecord={clientRecordDetailsToClientRecord(clientRecord!)}
                    organizationMedicalProfile={organizationMedicalProfile}
                    organizationClientRecordGroups={clientRecordGroups}
                />
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(ClientRecordDetails);
